// ResearchGob — the upgrade lab: kmidUpgradeCommand marks the HRC/VTS upgrade
// in-progress on the PLAYER (builders watch those flags), then UpgradeUpdate drips 3
// credits per update until the template's nUpgradeCost is paid and the flag flips to
// done (Player::SetUpgrades notifies the affected builders). Abort refunds; deactivate
// abandons WITHOUT refund. game/Research.cpp (the UpgradeForm + gaupg display tables
// are UI; the charged cost is the template's nUpgradeCost).

import type { SimContext } from '../context.ts';
import { StructGob } from './struct.ts';
import { StructConsts, InitStructConstsClass, kfUntcNotifyPowerLowHigh } from './unitconsts.ts';
import { kgtResearchCenter } from './gobtypes.ts';
import {
  kutResearchCenter,
  kutHumanResourceCenter,
  kutVehicleTransportStation,
} from '../player/side.ts';
import { kumReactor } from '../player/masks.ts';
import type { Player } from '../player/player.ts';
import {
  Message,
  knHandled,
  kstIdle,
  kmidReservedUpdate,
  kmidUpgradeCommand,
  kmidAbortUpgradeCommand,
  kmidSelfDestructCommand,
  kfUpgradeHrc,
  kfUpgradeVts,
  kfUpgradeHrcInProgress,
  kfUpgradeVtsInProgress,
} from '../sm/message.ts';
import { kutNone } from './builder.ts';
import { registerGobType } from './factory.ts';
import type { IniReader } from '../data/ini.ts';
import {
  ksfxNothing,
  ksfxResearchCenterAbortRepair,
  ksfxResearchCenterRepair,
  ksfxResearchCenterDamaged,
  ksfxResearchCenterSelect,
  ksfxResearchCenterDestroyed,
} from '../data/soundeffects.ts';

// game/Research.cpp:6 — credits of upgrade per update
export const kcUpdateCost = 3;

// game/Research.cpp:104-148
export function InitResearchClass(ctx: SimContext, pini: IniReader): boolean {
  const c = new StructConsts().bind(ctx);
  c.gt = kgtResearchCenter;
  c.ut = kutResearchCenter;
  c.umPrerequisites = kumReactor;
  c.wf |= kfUntcNotifyPowerLowHigh; // Research.cpp:124 — wants power notification
  // Sound effects (Research.cpp:113-120)
  c.sfxAbortRepair = ksfxResearchCenterAbortRepair;
  c.sfxRepair = ksfxResearchCenterRepair;
  c.sfxDamaged = ksfxResearchCenterDamaged;
  c.sfxSelect = ksfxResearchCenterSelect;
  c.sfxDestroyed = ksfxResearchCenterDestroyed;
  c.sfxImpact = ksfxNothing;
  c.sfxAbortUpgrade = ksfxResearchCenterAbortRepair;
  c.sfxUpgrade = ksfxResearchCenterRepair;
  return InitStructConstsClass(ctx, c, pini);
}

export class ResearchGob extends StructGob {
  m_utTarget = kutNone;
  m_nCreditsSpentOnUpgrade = 0;

  // game/Research.cpp:167-196 (knVerResearchGobState 3) — the CW sign-extension
  // workaround (255 -> kutNone) is preserved; the spent block exists only mid-upgrade
  override LoadState(pstm: import('../sm/stream.ts').Stream): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 3) return false;
    this.m_utTarget = (pstm.ReadByte() << 24) >> 24;
    if (this.m_utTarget === 255) this.m_utTarget = kutNone; // unreachable post-cast; kept
    if (this.m_utTarget !== kutNone) {
      this.m_nCreditsSpentOnUpgrade = pstm.ReadWord();
    }
    return super.LoadState(pstm);
  }

  override SaveState(pstm: import('../sm/stream.ts').Stream): boolean {
    pstm.WriteByte(3);
    pstm.WriteByte(this.m_utTarget & 0xff);
    if (this.m_utTarget !== kutNone) {
      pstm.WriteWord(this.m_nCreditsSpentOnUpgrade);
    }
    return super.SaveState(pstm);
  }

  // game/Research.cpp:159-163
  constructor(ctx: SimContext) {
    super(ctx, ctx.unitConsts[kutResearchCenter] as StructConsts);
  }

  // game/Research.cpp:331-386
  UpgradeUpdate(): void {
    const nCost = (this.ctx.unitConsts[this.m_utTarget] as StructConsts).nUpgradeCost;

    // (god mode pinned off in equivalence runs)
    {
      // either the default per-update amount or what's left
      let nCostPerUpdate = nCost - this.m_nCreditsSpentOnUpgrade;
      if (nCostPerUpdate > kcUpdateCost) nCostPerUpdate = kcUpdateCost;

      const nCredits = (this.m_pplr as Player).GetCredits() - nCostPerUpdate;
      if (nCredits >= 0) {
        (this.m_pplr as Player).SetCredits(nCredits, true);
        this.m_nCreditsSpentOnUpgrade += nCostPerUpdate;
      }
      this.WaitingForCredits(nCredits < 0);
    }

    if (this.m_nCreditsSpentOnUpgrade === nCost) {
      let wFlagOn: number;
      let wFlagOff: number;
      if (this.m_utTarget === kutHumanResourceCenter) {
        wFlagOn = kfUpgradeHrc;
        wFlagOff = kfUpgradeHrcInProgress;
      } else {
        wFlagOn = kfUpgradeVts;
        wFlagOff = kfUpgradeVtsInProgress;
      }
      let wUpgrades = (this.m_pplr as Player).GetUpgrades();
      wUpgrades &= ~wFlagOff;
      (this.m_pplr as Player).SetUpgrades(wUpgrades | wFlagOn);

      // (completion sfx + alert: host/local)

      this.m_utTarget = kutNone;
      this.m_nCreditsSpentOnUpgrade = 0;
      this.WaitingForCredits(false);
    }
  }

  // game/Research.cpp:401-414 — abandon and refund
  AbortUpgrade(): void {
    if (this.m_utTarget !== kutNone) {
      (this.m_pplr as Player).SetCredits(
        (this.m_pplr as Player).GetCredits() + this.m_nCreditsSpentOnUpgrade,
        false,
      );
      (this.m_pplr as Player).ModifyTotalCreditsConsumed(-this.m_nCreditsSpentOnUpgrade);
      (this.m_pplr as Player).SetUpgrades(
        (this.m_pplr as Player).GetUpgrades() & ~(kfUpgradeVtsInProgress | kfUpgradeHrcInProgress),
      );
      this.m_utTarget = kutNone;
      this.m_nCreditsSpentOnUpgrade = 0;
      this.WaitingForCredits(false);
      this.m_unvl.MinSkip();
    }
  }

  // game/Research.cpp:416-428 — takeover/destruction abandons WITHOUT refund
  override Deactivate(): void {
    if (this.m_utTarget !== kutNone) {
      (this.m_pplr as Player).SetUpgrades(
        (this.m_pplr as Player).GetUpgrades() & ~(kfUpgradeVtsInProgress | kfUpgradeHrcInProgress),
      );
      this.m_utTarget = kutNone;
      this.m_nCreditsSpentOnUpgrade = 0;
    }
    super.Deactivate();
  }

  // game/Research.cpp:264-329
  override ProcessStateMachineMessage(st: number, pmsg: Message): number {
    if (st === kstIdle) {
      switch (pmsg.mid) {
        case kmidUpgradeCommand:
          // The upgrade applies to the PLAYER; structures watch the flags
          if (pmsg.UpgradeCommand.wfUpgrade & kfUpgradeVts) {
            if (
              !((this.m_pplr as Player).GetUpgrades() & (kfUpgradeVts | kfUpgradeVtsInProgress))
            ) {
              (this.m_pplr as Player).SetUpgrades(
                (this.m_pplr as Player).GetUpgrades() | kfUpgradeVtsInProgress,
              );
              this.m_utTarget = kutVehicleTransportStation;
            }
          }

          if (pmsg.UpgradeCommand.wfUpgrade & kfUpgradeHrc) {
            if (
              !((this.m_pplr as Player).GetUpgrades() & (kfUpgradeHrc | kfUpgradeHrcInProgress))
            ) {
              (this.m_pplr as Player).SetUpgrades(
                (this.m_pplr as Player).GetUpgrades() | kfUpgradeHrcInProgress,
              );
              this.m_utTarget = kutHumanResourceCenter;
            }
          }

          // Wake up and start costing the upgrade
          this.m_unvl.MinSkip();
          return knHandled;

        case kmidAbortUpgradeCommand:
          this.AbortUpgrade();
          return knHandled;

        case kmidSelfDestructCommand:
          this.AbortUpgrade();
          this.SelfDestruct();
          return knHandled;

        case kmidReservedUpdate:
          // No animation while power is low (kmidPowerLowHigh wakes us on change)
          if (!(this.m_pplr as Player).IsPowerLow()) {
            this.AdvanceAnimation(this.m_ani);
          }

          if (this.m_utTarget !== kutNone) {
            this.UpgradeUpdate();
            this.MarkRedraw();
            this.m_unvl.MinSkip();
          }
          this.DefUpdate();
          return knHandled;
      }
      return super.ProcessStateMachineMessage(st, pmsg);
    }

    return super.ProcessStateMachineMessage(st, pmsg);
  }
}

// self-register (see factory.ts)
registerGobType(kgtResearchCenter, (ctx) => new ResearchGob(ctx));
